import { parseFloatSafe } from "@omni/utils/number.utils";

import ResultField from "@/components/Calculator/Components/ResultField/resultField.component";
import ResultTitle from "@/components/Calculator/Components/ResultTitle/resultTitle.component";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { DamImage } from "@/utils/dam.utils";

import { CalculationGroup } from "../../Components/CalculationGroup/calculationGroup.component";

type Props = {
    brickWidth: string;
    brickLength: string;
    wallArea: number;
    numberOfBricksHalf: number;
    numberOfBricksFull: number;
    mortarVolumeHalf: number;
    mortarVolumeFull: number;
    jointCementVolume: number;
    mailParams: Record<string, string>;
};

const Result = ({
    brickWidth,
    brickLength,
    wallArea,
    numberOfBricksHalf,
    numberOfBricksFull,
    mortarVolumeHalf,
    mortarVolumeFull,
    jointCementVolume,
    mailParams
}: Props) => {
    const { t } = useGranite();

    const wallWidthInMetersHalf = parseFloatSafe(brickWidth, 0) * 0.01;
    const wallWidthInMetersFull = parseFloatSafe(brickLength, 0) * 0.01;

    // half stonewall
    const cementInKgHalf = mortarVolumeHalf * 300 * 1.1;
    const sandInKgHalf = mortarVolumeHalf * 1500 * 1.1;
    const waterHalf = mortarVolumeHalf * 150;
    const cementInBagsOf20KgHalf = Math.ceil(cementInKgHalf / 20);
    const cementInBagsOf25KgHalf = Math.ceil(cementInKgHalf / 25);
    const sandInBagsOf20KgHalf = Math.ceil(sandInKgHalf / 20);
    const sandInBagsOf25KgHalf = Math.ceil(sandInKgHalf / 25);

    // full stonewall
    const cementInKgFull = mortarVolumeFull * 300 * 1.1;
    const sandInKgFull = mortarVolumeFull * 1500 * 1.1;
    const waterFull = mortarVolumeFull * 150;
    const cementInBagsOf20KgFull = Math.ceil(cementInKgFull / 20);
    const cementInBagsOf25KgFull = Math.ceil(cementInKgFull / 25);
    const sandInBagsOf20KgFull = Math.ceil(sandInKgFull / 20);
    const sandInBagsOf25KgFull = Math.ceil(sandInKgFull / 25);

    // jointcement single side
    const jointCementInKg = jointCementVolume * 350 * 1.1;
    const jointSandInKg = jointCementVolume * 1500 * 1.1;
    const jointWater = jointCementVolume * 175;
    const jointCementInBagsOf20Kg = Math.ceil(jointCementInKg / 20);
    const jointCementInBagsOf25Kg = Math.ceil(jointCementInKg / 25);
    const jointSandInBagsOf20Kg = Math.ceil(jointSandInKg / 20);
    const jointSandInBagsOf25Kg = Math.ceil(jointSandInKg / 25);

    // jointcement double side
    const jointCementInKgDoubleSide = 2 * jointCementVolume * 350 * 1.1;
    const jointSandInKgDoubleSide = 2 * jointCementVolume * 1500 * 1.1;
    const jointWaterDoubleSide = 2 * jointCementVolume * 175;
    const jointCementInBagsOf20KgDoubleSide = Math.ceil(
        (2 * jointCementInKg) / 20
    );
    const jointCementInBagsOf25KgDoubleSide = Math.ceil(
        (2 * jointCementInKg) / 25
    );
    const jointSandInBagsOf20KgDoubleSide = Math.ceil((2 * jointSandInKg) / 20);
    const jointSandInBagsOf25KgDoubleSide = Math.ceil((2 * jointSandInKg) / 25);

    return (
        <div className={"mt-4"}>
            <ResultTitle mailParams={mailParams} />
            <Row id="resultTop">
                <ResultField
                    copy={t("calculator.brickCalculator.wallArea")}
                    value={wallArea.toFixed(2)}
                    unit={"m"}
                    sup={2}
                    md={6}
                />
            </Row>
            <Row id="resultBottom">
                <Col
                    id="half"
                    sm={6}
                >
                    <Row>
                        <Col
                            xs={12}
                            md={6}
                            lg={6}
                        >
                            <h3 className="d-sm-block">
                                {t("calculator.brickCalculator.halfBrick")}
                            </h3>
                            <p>
                                {t(
                                    "calculator.brickCalculator.halfBrickDetails"
                                )}
                            </p>
                        </Col>
                        <Col
                            xs={12}
                            md={6}
                            lg={6}
                        >
                            <img
                                className={"my-2 w-100"}
                                src={DamImage.calculator.halfBrick()}
                                alt={"muur-halve-steen"}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <ResultField
                            copy={t("calculator.brickCalculator.wallWidth")}
                            value={wallWidthInMetersHalf.toFixed(2)}
                            unit={"m"}
                            md={6}
                        />
                        <ResultField
                            copy={t(
                                "calculator.brickCalculator.numberOfBricks"
                            )}
                            value={numberOfBricksHalf.toFixed(0)}
                            md={6}
                        />
                    </Row>
                    <Row>
                        <Col xs={12}>
                            <h4>{t("calculator.brickCalculator.grout")}</h4>
                        </Col>
                    </Row>
                    <Row>
                        <ResultField
                            copy={t("calculator.volumeNeeded")}
                            value={mortarVolumeHalf.toFixed(2)}
                            md={6}
                            unit={"m"}
                            sup={3}
                        />
                    </Row>
                    <CalculationGroup>
                        <ResultField
                            copy={t("calculator.cement")}
                            value={cementInKgHalf.toFixed(2)}
                            md={6}
                            unit={"kg"}
                        />
                        <ResultField
                            copy={t("calculator.nrBags20Kg")}
                            value={cementInBagsOf20KgHalf}
                            md={6}
                        />
                        <ResultField
                            className={"offset-md-6"}
                            copy={t("calculator.nrBags25Kg")}
                            value={cementInBagsOf25KgHalf}
                            md={6}
                        />
                    </CalculationGroup>
                    <CalculationGroup>
                        <ResultField
                            copy={t("calculator.rhineSand")}
                            value={sandInKgHalf.toFixed(2)}
                            md={6}
                            unit={"kg"}
                        />
                        <ResultField
                            copy={t("calculator.nrBags20Kg")}
                            value={sandInBagsOf20KgHalf.toFixed(0)}
                            md={6}
                        />
                        <ResultField
                            className={"offset-md-6"}
                            copy={t("calculator.nrBags25Kg")}
                            value={sandInBagsOf25KgHalf.toFixed(0)}
                            md={6}
                        />
                    </CalculationGroup>
                    <Row>
                        <ResultField
                            copy={t("calculator.water")}
                            value={waterHalf.toFixed(2)}
                            md={6}
                            unit={"l"}
                        />
                    </Row>
                    <Row>
                        <Col xs={12}>
                            <h4>
                                {t("calculator.brickCalculator.jointCement")}
                            </h4>
                        </Col>
                    </Row>
                    <Row>
                        <ResultField
                            copy={t("calculator.volumeNeeded")}
                            value={jointCementVolume.toFixed(2)}
                            md={6}
                            unit={"m"}
                            sup={3}
                        />
                    </Row>
                    <CalculationGroup>
                        <ResultField
                            copy={t("calculator.cement")}
                            value={jointCementInKg.toFixed(2)}
                            md={6}
                            unit={"kg"}
                        />
                        <ResultField
                            copy={t("calculator.nrBags20Kg")}
                            value={jointCementInBagsOf20Kg.toFixed(0)}
                            md={6}
                        />
                        <ResultField
                            className={"offset-md-6"}
                            copy={t("calculator.nrBags25Kg")}
                            value={jointCementInBagsOf25Kg.toFixed(0)}
                            md={6}
                        />
                    </CalculationGroup>
                    <CalculationGroup>
                        <ResultField
                            copy={t("calculator.whiteSand")}
                            value={jointSandInKg.toFixed(2)}
                            md={6}
                            unit={"kg"}
                        />
                        <ResultField
                            copy={t("calculator.nrBags20Kg")}
                            value={jointSandInBagsOf20Kg.toFixed(0)}
                            md={6}
                        />
                        <ResultField
                            className={"offset-md-6"}
                            copy={t("calculator.nrBags25Kg")}
                            value={jointSandInBagsOf25Kg.toFixed(0)}
                            md={6}
                        />
                    </CalculationGroup>
                    <Row>
                        <ResultField
                            copy={t("calculator.water")}
                            value={jointWater.toFixed(2)}
                            md={6}
                            unit={"l"}
                        />
                    </Row>
                </Col>
                <Col
                    id="fullBrick"
                    sm={6}
                >
                    <Row>
                        <Col
                            xs={12}
                            md={6}
                            lg={6}
                        >
                            <h3 className="d-sm-block">
                                {t("calculator.brickCalculator.fullBrick")}
                            </h3>
                            <p className="d-sm-block">
                                {t(
                                    "calculator.brickCalculator.fullBrickDetails"
                                )}
                            </p>
                        </Col>
                        <Col
                            xs={12}
                            md={6}
                            lg={6}
                        >
                            <img
                                className={"my-2 w-100"}
                                src={DamImage.calculator.fullBrick()}
                                alt={"muur-volle-steen"}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <ResultField
                            copy={t("calculator.brickCalculator.wallWidth")}
                            value={wallWidthInMetersFull.toFixed(2)}
                            unit={"m"}
                            md={6}
                        />
                        <ResultField
                            copy={t(
                                "calculator.brickCalculator.numberOfBricks"
                            )}
                            value={numberOfBricksFull.toFixed(0)}
                            md={6}
                        />
                    </Row>
                    <Row>
                        <Col xs={12}>
                            <h4>{t("calculator.brickCalculator.grout")}</h4>
                        </Col>
                    </Row>
                    <Row>
                        <ResultField
                            copy={t("calculator.volumeNeeded")}
                            value={mortarVolumeFull.toFixed(2)}
                            md={6}
                            unit={"m"}
                            sup={3}
                        />
                    </Row>
                    <CalculationGroup>
                        <ResultField
                            copy={t("calculator.cement")}
                            value={cementInKgFull.toFixed(2)}
                            md={6}
                            unit={"kg"}
                        />
                        <ResultField
                            copy={t("calculator.nrBags20Kg")}
                            value={cementInBagsOf20KgFull}
                            md={6}
                        />
                        <ResultField
                            className={"offset-md-6"}
                            copy={t("calculator.nrBags25Kg")}
                            value={cementInBagsOf25KgFull}
                            md={6}
                        />
                    </CalculationGroup>
                    <CalculationGroup>
                        <ResultField
                            copy={t("calculator.rhineSand")}
                            value={sandInKgFull.toFixed(2)}
                            md={6}
                            unit={"kg"}
                        />
                        <ResultField
                            copy={t("calculator.nrBags20Kg")}
                            value={sandInBagsOf20KgFull}
                            md={6}
                        />
                        <ResultField
                            className={"offset-md-6"}
                            copy={t("calculator.nrBags25Kg")}
                            value={sandInBagsOf25KgFull}
                            md={6}
                        />
                    </CalculationGroup>
                    <Row>
                        <ResultField
                            copy={t("calculator.water")}
                            value={waterFull.toFixed(2)}
                            md={6}
                            unit={"l"}
                        />
                    </Row>
                    <Row>
                        <Col xs={12}>
                            <h4>
                                {t("calculator.brickCalculator.jointCement")}
                            </h4>
                        </Col>
                    </Row>
                    <Row>
                        <ResultField
                            copy={t("calculator.volumeNeeded")}
                            value={jointCementVolume.toFixed(2)}
                            md={6}
                            unit={"m"}
                            sup={3}
                        />
                    </Row>
                    <CalculationGroup>
                        <ResultField
                            copy={t("calculator.cement")}
                            value={jointCementInKgDoubleSide.toFixed(2)}
                            md={6}
                            unit={"kg"}
                        />
                        <ResultField
                            copy={t("calculator.nrBags20Kg")}
                            value={jointCementInBagsOf20KgDoubleSide.toFixed(0)}
                            md={6}
                        />
                        <ResultField
                            className={"offset-md-6"}
                            copy={t("calculator.nrBags25Kg")}
                            value={jointCementInBagsOf25KgDoubleSide.toFixed(0)}
                            md={6}
                        />
                    </CalculationGroup>
                    <CalculationGroup>
                        <ResultField
                            copy={t("calculator.whiteSand")}
                            value={jointSandInKgDoubleSide.toFixed(2)}
                            md={6}
                            unit={"kg"}
                        />
                        <ResultField
                            copy={t("calculator.nrBags20Kg")}
                            value={jointSandInBagsOf20KgDoubleSide.toFixed(0)}
                            md={6}
                        />
                        <ResultField
                            className={"offset-md-6"}
                            copy={t("calculator.nrBags25Kg")}
                            value={jointSandInBagsOf25KgDoubleSide.toFixed(0)}
                            md={6}
                        />
                    </CalculationGroup>
                    <Row>
                        <ResultField
                            copy={t("calculator.water")}
                            value={jointWaterDoubleSide.toFixed(2)}
                            md={6}
                            unit={"l"}
                        />
                    </Row>
                </Col>
            </Row>
        </div>
    );
};

export default Result;
